/**
 * @file Helpers for tasks within the kanban
 */

import { PAGINATION__MAX_ITEMS_PER_PAGE } from '../../constants/forms';
import {
  Message,
  MessageStatus,
  TasksDocument,
  TasksQueryVariables,
  Topic,
} from '../../generated/graphql';
import { MessageFeed } from '../../models/message';

/**
 * Helps us to identify if the message is a task. A message is a task if it
 * has a due date set and status.
 *
 * @param message The message we want to verify
 * @returns       Whether the message is a task
 */
export const getIsTask = <T extends Pick<Message, 'status'>>(
  message: T,
): message is T & { status: MessageStatus } => {
  const { status = null } = message;
  return status !== null;
};

/**
 * Get the variables for tasks query
 *
 * @param topicId    Topic the tasks belong to
 * @param status     Task status
 * @param members    Member ids we need to filter tasks by
 * @param hasDueDate Whether to fetch only with/without due date
 * @returns          Tasks query options object
 */
export const getTasksQueryOptions = (
  topicId: Topic['id'] | null,
  status: MessageStatus,
  members: string[] | null | undefined,
  hasDueDate?: boolean,
) => ({
  pause: topicId === null,
  query: TasksDocument,
  variables: {
    filter: {
      hasDueDate,
      members: members ?? [],
      status,
      topicId: topicId ?? '',
    },
    pagination: { before: '', last: PAGINATION__MAX_ITEMS_PER_PAGE },
  } satisfies TasksQueryVariables,
});

/**
 * Should insert after the last item with due date, and before first item with no due date
 *
 * @param task     The task to insert
 * @param taskItem The task from the list
 * @returns        Boolean
 */
const getIsInsertWithDueDateEnd = (task: MessageFeed, taskItem: MessageFeed) =>
  task.dueDate !== null && taskItem.dueDate === null;

/**
 * Get whether the task should be sorted by due date
 *
 * @param task     The task to insert
 * @param taskItem The task from the list
 * @returns        True if it should, otherwise false
 */
const getShouldSortByDueDate = (task: MessageFeed, taskItem: MessageFeed) =>
  task.dueDate !== null && taskItem.dueDate !== null;

/**
 * Get whether the task should be sorted by createdAt
 *
 * @param task     The task to insert
 * @param taskItem The task from the list
 * @returns        True if it should, otherwise false
 */
const getShouldSortByCreatedAt = (task: MessageFeed, taskItem: MessageFeed) =>
  task.dueDate === null && taskItem.dueDate === null;

/**
 * Get whether to insert task before a certain task item in the list
 *
 * @param task     The task to insert
 * @param taskItem The task from the list
 * @returns        Boolean
 */
const getShouldInsertTask = (task: MessageFeed, taskItem: MessageFeed) => {
  const insertedTaskTimestamp = new Date(
    task.dueDate ?? task.createdAt,
  ).getTime();
  const existingTaskTimestamp = new Date(
    taskItem.dueDate ?? taskItem.createdAt,
  ).getTime();

  if (getShouldSortByDueDate(task, taskItem)) {
    return insertedTaskTimestamp < existingTaskTimestamp; // Ascending for dueDate
  }

  if (getShouldSortByCreatedAt(task, taskItem)) {
    return insertedTaskTimestamp > existingTaskTimestamp; // Descending for createdAt
  }

  return false;
};

/**
 * Inserts a task into the tasks array while maintaining order based on due dates.
 *
 * @param task  The task to insert
 * @param tasks The array of tasks
 * @returns     A new array of tasks with the task inserted
 */
export const insertTask = (task: MessageFeed, tasks: MessageFeed[]) => {
  const isTask = getIsTask(task);

  if (isTask === false) {
    return tasks;
  }

  const insertIndex = tasks.findIndex(taskItem => {
    // Insert after the last item with due date, and before first item with no due date
    if (getIsInsertWithDueDateEnd(task, taskItem)) {
      return true;
    }

    // Tasks with no due date, come after tasks with due date
    if (task.dueDate === null && taskItem.dueDate !== null) {
      return false;
    }

    return getShouldInsertTask(task, taskItem);
  });

  if (insertIndex === -1) {
    return [...tasks, task];
  }

  const finalTasks = [...tasks];
  finalTasks.splice(insertIndex, 0, task);

  return finalTasks;
};
